import { inject, injectable } from 'inversify';

import { ProjectsFacade } from '../facade/projects.facade';
import { ProjectBody, PublicProjectBody } from '../models/project.model';
import { TYPES } from '../projects.types';
import { FontSource } from '../store/fonts.slice';
import { ProjectStoreInjector } from '../store/projects.store';

export type Font = {
  fontFamily: string;
  fontStyle: string;
  fontWeight: string;
  fontWidth?: string;
  customFontId: string | null;
};

export type CustomFontData = {
  id: number;
  fontFamily: string;
  variants: Array<{
    id: number;
    fontStyle: string;
    fontWeight: string;
    fontWidth: string;
    source: string;
  }>;
};

const FONT_DEFAULTS = {
  STYLE: 'normal',
  WEIGHT: '400',
  STYLE_ITALIC: 'italic',
  WIDTH: 'normal',
} as const;

/**
 * Manages the extraction and loading of fonts, distinguishing between
 * Google Fonts (fetched via API) and potentially internal/local fonts.
 */
@injectable()
export class FontManager {
  private obfuscationEnabled = false;
  private staticUrl = '';
  private fontPathPrefix = '';

  constructor(
    @inject(TYPES.ProjectsFacade)
    private readonly projectFacade: ProjectsFacade,
    @inject(TYPES.ProjectsStore)
    private readonly projectStore: ProjectStoreInjector
  ) {}

  public enableObfuscation(staticUrl: string, fontPathPrefix?: string) {
    this.obfuscationEnabled = true;
    this.staticUrl = staticUrl;

    if (fontPathPrefix) {
      this.fontPathPrefix = fontPathPrefix;
    }
  }

  /**
   * Extracts font information from SVG elements. It aggregates unique styles and weights
   * for each font family found.
   * @param document The HTMLElement (e.g., an SVG document) to extract fonts from.
   * @returns A Record mapping font family names to aggregated Font objects.
   */
  public extractFontsFromSVG(document: HTMLElement): Record<string, Font> {
    const extractedFontsMap: Map<string, Font> = new Map();
    const elements = document.querySelectorAll('[font-family]');

    elements.forEach((element) => {
      const fontFamily = element.getAttribute('font-family')?.replace(/^["']+|["']+$/g, '');

      if (!fontFamily) return;

      const fontStyle = element.getAttribute('font-style') ?? FONT_DEFAULTS.STYLE;
      const fontWeight = element.getAttribute('font-weight') ?? FONT_DEFAULTS.WEIGHT;

      const fontWidth =
        element.getAttribute('font-width') ??
        element.getAttribute('font-stretch') ??
        FONT_DEFAULTS.WIDTH;

      const customFontId = element.getAttribute('custom-font-id') ?? null;

      extractedFontsMap.set(fontFamily, {
        fontFamily,
        fontStyle,
        fontWeight,
        fontWidth,
        customFontId,
      });
    });

    return Object.fromEntries(extractedFontsMap);
  }

  /**
   * Checks if a font family name is an obfuscated alias
   * (Longer than 20 chars, Base64url encoded strings).
   * @param fontFamily The font family name to check.
   * @returns True if the font family is an obfuscated alias, false otherwise.
   */
  private isObfuscatedFontAlias(fontFamily: string): boolean {
    if (!this.obfuscationEnabled) {
      return false;
    }

    return /^[A-Za-z0-9_-]{20,}$/.test(fontFamily);
  }

  /**
   * Checks if a font is a Google Font based on its family name and custom font ID.
   * @param fontFamily The font family name.
   * @param customFontId The custom font ID.
   * @returns True if the font is a Google Font, false otherwise.
   */
  private checkIfGoogleFont(fontFamily: Font['fontFamily'], customFontId: Font['customFontId']) {
    // If it has a custom font ID or is obfuscated, it's NOT a Google Font
    if (customFontId !== null || this.isObfuscatedFontAlias(fontFamily)) {
      return false;
    }

    // If no custom font ID and has a valid font family name, it's a Google Font
    return typeof fontFamily === 'string' && fontFamily.trim() !== '';
  }

  /**
   * Checks if a Google Font is already loaded by checking the store.
   * @param fontFamily The font family name.
   * @returns True if the font is already in the store (loaded).
   */
  private isGoogleFontLoaded(fontFamily: string): boolean {
    return this.projectStore.getState().fonts.googleFonts.includes(fontFamily);
  }

  /**
   * Checks if an internal font is already loaded by checking the store.
   * @param customFontId The custom font ID.
   * @returns True if the font is already in the store (loaded).
   */
  private isInternalFontLoaded(customFontId: number): boolean {
    const libraryFont = this.projectStore.getState().fonts.library[customFontId];

    return !!libraryFont;
  }

  /**
   * Registers a Google Font in the store after successful loading.
   * @param fontFamily The Google Font family name to register.
   */
  private registerGoogleFont(fontFamily: string): void {
    this.projectStore.getState().fonts.addGoogleFont(fontFamily);
  }

  /**
   * Loads all required Google Fonts based on the aggregated font data.
   * It constructs a single Google Fonts API URL and appends it as a <link> tag
   * to the document head to avoid CORS issues and optimize loading.
   * @param fontsToLoad A Record<string, Font> containing the aggregated font data.
   * @returns A Promise that resolves when the font link has been added to the DOM.
   */

  public async loadGoogleFonts(fontsToLoad: Record<string, Font>): Promise<void> {
    // Group fonts by family name to load all variants at once
    const fontFamilies = new Set<string>();

    for (const key in fontsToLoad) {
      const fontKey = fontsToLoad[key];

      // Check if font family is already loaded in store
      if (this.isGoogleFontLoaded(fontKey.fontFamily)) {
        continue;
      }

      fontFamilies.add(fontKey.fontFamily);
    }

    // If no new fonts to load, return early
    if (fontFamilies.size === 0) {
      return;
    }

    // Load each unique font family with all its variants using link tags
    for (const fontFamily of fontFamilies) {
      // Double-check if font is already loaded in store before proceeding
      if (this.isGoogleFontLoaded(fontFamily)) {
        continue;
      }

      try {
        // Create Google Font URL with all available variants (weights 100-900, normal and italic)
        const googleFontUrl = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
          / /g,
          '+'
        )}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap`;

        // Check if link tag already exists
        const existingLink = document.querySelector(`link[href="${googleFontUrl}"]`);

        if (!existingLink) {
          // Create and append link tag to document head
          const linkElement = document.createElement('link');

          linkElement.rel = 'stylesheet';
          linkElement.href = googleFontUrl;
          linkElement.type = 'text/css';

          document.head.appendChild(linkElement);
        }

        // Register font family in store only after successful creation
        this.registerGoogleFont(fontFamily);
      } catch (error) {
        // No need to remove from store since we haven't registered it yet
        console.error(`Failed to load Google Font ${fontFamily}:`, error);
      }
    }
  }

  private async registerFontFaces(fontFaceData: Record<number, CustomFontData>) {
    const loadPromises: Promise<unknown>[] = [];

    for (const key in fontFaceData) {
      const fontData = fontFaceData[key];

      // Store is the source of truth - check if font is already loaded
      if (this.isInternalFontLoaded(fontData.id)) continue;

      // Store font metadata in the store
      this.projectStore.getState().fonts.setLibraryFont({
        id: fontData.id,
        family: fontData.fontFamily,
        sources: fontData.variants.map(
          (variant) =>
            ({
              id: variant.id,
              source: variant.source,
              style: variant.fontStyle,
              weight: variant.fontWeight,
              width: variant.fontWidth,
            } as FontSource)
        ),
      });

      // Queue all variants for parallel loading
      for (const variant of fontData.variants) {
        const fontFace = new FontFace(fontData.fontFamily, `url(${variant.source})`, {
          weight: variant.fontWeight,
          style: variant.fontStyle,
          stretch: variant.fontWidth,
        });

        document.fonts.add(fontFace);

        loadPromises.push(
          fontFace.load().catch((error) => {
            console.error(
              `Failed to load font variant ${fontData.fontFamily} (weight: ${variant.fontWeight}, style: ${variant.fontStyle}):`,
              error
            );
          })
        );
      }
    }

    if (loadPromises.length > 0) {
      await Promise.all(loadPromises);
    }
  }

  /**
   * Extracts variant data (weight and style) from a variant string.
   * @param variant The variant string from the font source (e.g., '400', '700italic').
   * @returns An object containing the weight and style extracted from the variant.
   */
  private extractVariantData(variant: string): {
    weight: string;
    style: string;
    width: string;
  } {
    if (!variant) {
      return {
        weight: FONT_DEFAULTS.WEIGHT,
        style: FONT_DEFAULTS.STYLE,
        width: FONT_DEFAULTS.WIDTH,
      };
    }

    const weight = parseInt(variant);
    const style = variant.replace(weight.toString(), '') || FONT_DEFAULTS.STYLE;

    return { weight: weight.toString(), style, width: FONT_DEFAULTS.WIDTH };
  }

  /**
   * Prepares font face data from a collection of fonts.
   * Processes sources, extracts variants, and builds FontFaceData structure for browser registration.
   * @param fonts Array of font objects with id, data.family, and data.sources
   * @param svgFontFamilyMap Optional map of font IDs to obfuscated SVG font family names
   * @returns Prepared font face data ready for registration with the browser
   */
  private prepareFontFaceData(
    fonts: Array<{ id: number; data: { family: string; sources: any[] } }>,
    svgFontFamilyMap?: Map<number, string>
  ): Record<number, CustomFontData> {
    const fontFaceData: Record<number, CustomFontData> = {};

    for (const font of fonts) {
      const sources = Array.isArray(font.data.sources) ? font.data.sources : [];

      for (const source of sources) {
        // Skip font source if variant is missing
        if (!source.variant) {
          continue;
        }

        const { style, weight, width } = this.extractVariantData(source.variant);

        const sourceUrl =
          this.fontPathPrefix && source.source.startsWith(this.fontPathPrefix)
            ? `${this.staticUrl}${source.source}`
            : source.source;

        // Use SVG font family if available (for obfuscated fonts), otherwise use API family
        const fontFamily = svgFontFamilyMap?.get(font.id) ?? font.data.family;
        const variants = fontFaceData[font.id]?.variants ?? [];

        fontFaceData[font.id] = {
          id: font.id,
          fontFamily: fontFamily,
          variants: [
            ...variants,
            {
              id: source.id ?? 0,
              fontStyle: style,
              fontWeight: weight,
              fontWidth: width,
              source: sourceUrl,
            },
          ],
        };
      }
    }

    return fontFaceData;
  }

  /**
   * Placeholder for loading internal/local fonts.
   * This method would handle fonts that are not fetched from Google Fonts.
   * @param fontsToLoad A Record<string, Font> containing internal font data.
   */
  public async loadInternalFonts(fontsToLoad: Record<string, Font>): Promise<void> {
    const uniqueFontIds = new Set<number>();
    const svgFontFamilyMap = new Map<number, string>(); // Maps font ID to SVG fontFamily
    let hasObfuscatedFonts = false;

    for (const key in fontsToLoad) {
      const font = fontsToLoad[key];
      const customFontId = font.customFontId;

      if (this.isObfuscatedFontAlias(font.fontFamily)) {
        hasObfuscatedFonts = true;

        if (customFontId) {
          const customFontIdNum = Number(customFontId);

          if (!this.isInternalFontLoaded(customFontIdNum)) {
            uniqueFontIds.add(customFontIdNum);
            svgFontFamilyMap.set(customFontIdNum, font.fontFamily);
          }
        }

        continue;
      }

      if (customFontId) {
        const customFontIdNum = Number(customFontId);

        // Check if font is already loaded in store
        if (this.isInternalFontLoaded(customFontIdNum)) {
          continue;
        }

        uniqueFontIds.add(customFontIdNum);
      }
    }

    // If all fonts are already loaded and no obfuscated fonts to check, return early
    if (uniqueFontIds.size === 0 && !hasObfuscatedFonts) {
      return;
    }

    const fontPromises = Array.from(uniqueFontIds).map((id) => this.projectFacade.loadFontData(id));
    const fontsToProcess: Array<{ id: number; data: { family: string; sources: any[] } }> =
      await Promise.all(fontPromises);

    const fontFaceData = this.prepareFontFaceData(fontsToProcess, svgFontFamilyMap);

    if (Object.keys(fontFaceData).length > 0) {
      await this.registerFontFaces(fontFaceData);
    }
  }

  /**
   * Preloads all project fonts for a given project.
   * Called once during project initialization to populate the FontsStore and register fonts with the browser.
   * This ensures all project fonts are available before pages render.
   *
   * @param projectData The project data containing encoded_id and other properties
   */
  public async loadProjectFonts(projectData: ProjectBody | PublicProjectBody): Promise<void> {
    try {
      if (!projectData.encoded_id) {
        return;
      }

      // Load all project fonts from API
      const allProjectFonts = await this.projectFacade.loadPublicProjectFonts(
        projectData.encoded_id
      );

      if (!Array.isArray(allProjectFonts) || allProjectFonts.length === 0) {
        return;
      }

      // Prepare font face data using shared helper and register with the browser
      const fontFaceData = this.prepareFontFaceData(allProjectFonts);

      if (Object.keys(fontFaceData).length > 0) {
        await this.registerFontFaces(fontFaceData);
      }
    } catch (error) {
      // Log error but don't block project loading
      console.warn('Failed to preload project fonts:', error);
    }
  }

  /**
   * Orchestrates the loading of extracted fonts from a page's SVG.
   * Separates Google Fonts from internal/local fonts and loads each type.
   * Called for each page during rendering.
   *
   * @param extractedFonts The Record<string, Font> object returned by extractFontsFromSVG.
   */
  public async loadFonts(extractedFonts: Record<string, Font>): Promise<void> {
    const googleFonts: Record<string, Font> = {};
    const internalFonts: Record<string, Font> = {};

    for (const key in extractedFonts) {
      const font = extractedFonts[key];
      const fontFamily = font.fontFamily;
      const customFontId = font.customFontId;

      if (this.checkIfGoogleFont(fontFamily, customFontId)) {
        googleFonts[key] = font;
      } else {
        internalFonts[key] = font;
      }
    }

    if (Object.keys(googleFonts).length > 0) {
      await this.loadGoogleFonts(googleFonts);
    }

    if (Object.keys(internalFonts).length > 0) {
      await this.loadInternalFonts(internalFonts);
    }
  }
}
